/**
 * Trang chu sau khi dang nhap.
 *
 * Muc tieu: dua nguoi choi den noi ho muon den trong mot lan bam. Khong dat
 * noi dung tiep thi dai o day — nguoi da dang nhap vao de choi, khong de doc.
 */

import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatMoney } from '../lib/money';
import { useSession, usePrimaryWallet } from '../lib/session';
import { Amount, Button, Card, Skeleton } from '../components/ui';
import styles from './Home.module.css';

const PRODUCTS = [
  { to: '/the-thao', label: 'Thể thao', note: 'Kèo trước trận và trực tiếp' },
  { to: '/song-bai', label: 'Sòng bài trực tiếp', note: 'Baccarat, Blackjack, Roulette' },
  { to: '/quay-hu', label: 'Quay hũ', note: 'Hũ tích lũy nhiều mức' },
  { to: '/xo-so', label: 'Xổ số & Keno', note: 'Kỳ quay có kiểm chứng công bằng' },
];

export function HomePage(): JSX.Element {
  const { isAuthenticated, me } = useSession();
  const wallet = usePrimaryWallet();

  const loyalty = useQuery({
    queryKey: ['loyalty'],
    queryFn: api.promotions.loyalty,
    enabled: isAuthenticated,
  });

  if (!isAuthenticated) {
    return (
      <div className={styles.guest}>
        <h1 className={styles.guestTitle}>Thể thao · Sòng bài · Xổ số</h1>
        <p className={styles.guestText}>
          Đăng nhập để xem sảnh trò chơi và kèo thể thao dành riêng cho tài khoản.
        </p>
        <div className={styles.guestActions}>
          <Link to="/dang-ky"><Button size="lg">Tạo tài khoản</Button></Link>
          <Link to="/dang-nhap"><Button size="lg" variant="secondary">Đăng nhập</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.summary}>
        <div className={styles.greeting}>
          <span className={styles.greetingLabel}>Xin chào</span>
          <span className={styles.greetingName}>{me?.username}</span>
        </div>

        {wallet && (
          <div className={styles.summaryBalance}>
            <span className={styles.summaryLabel}>Số dư khả dụng</span>
            <Amount value={formatMoney(wallet.available)} currency={wallet.currency} size="lg" />
          </div>
        )}

        {loyalty.isLoading ? (
          <Skeleton height={48} width={160} />
        ) : loyalty.data?.loyalty ? (
          <div className={styles.tier}>
            <span className={styles.summaryLabel}>Hạng thành viên</span>
            <span className={styles.tierName}>
              {loyalty.data.loyalty.tier_name ?? loyalty.data.loyalty.tier_code}
            </span>
            {loyalty.data.loyalty.next_tier && (
              <span className={styles.tierNext}>
                Còn <span className="numeric">{loyalty.data.loyalty.next_tier.points_needed}</span>{' '}
                điểm lên {loyalty.data.loyalty.next_tier.name}
              </span>
            )}
          </div>
        ) : null}
      </div>

      <div className={styles.products}>
        {PRODUCTS.map((p) => (
          <Link key={p.to} to={p.to} className={styles.product}>
            <span className={styles.productLabel}>{p.label}</span>
            <span className={styles.productNote}>{p.note}</span>
          </Link>
        ))}
      </div>

      <Card title="Chơi có trách nhiệm">
        <p className={styles.rgText}>
          Có thể tự đặt hạn mức nạp, hạn mức cược và hạn mức lỗ bất cứ lúc nào. Siết hạn mức có
          hiệu lực ngay; nới hạn mức chỉ có hiệu lực sau thời gian chờ.
        </p>
        <div className={styles.rgActions}>
          <Link to="/tai-khoan/han-muc">
            <Button size="sm" variant="secondary">Đặt hạn mức</Button>
          </Link>
          <Link to="/tai-khoan/tam-nghi">
            <Button size="sm" variant="ghost">Tạm nghỉ</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
